import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import { logoutCliente } from '@/utils/clienteAuth'
import { clearGuestSession } from '@/utils/guestAuth'

const STORAGE_KEY = 'agendamentos-app:user'
const SESSION_KEYS = [
  'agendamentos-app:user',
  'agendamentos-app:cliente:token',
  'agendamentos-app:admin:token',
  'agendamentos-app:admin:email',
  'agendamentos-app:profissional:token',
  'agendamentos-app:profissional:data',
]

export interface User {
  id: number
  nome: string
  telefone: string | null
  email: string
  avatar_url: string | null
}

function loadUser(): User | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as User
    return data && typeof data.id === 'number' && data.email ? data : null
  } catch {
    return null
  }
}

function saveUser(user: User | null) {
  if (user) localStorage.setItem(STORAGE_KEY, JSON.stringify(user))
  else localStorage.removeItem(STORAGE_KEY)
}

type UserContextValue = {
  user: User | null
  setUser: (user: User | null) => void
  logout: () => void
}

const UserContext = createContext<UserContextValue | null>(null)

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(loadUser)

  const setUser = useCallback((u: User | null) => {
    setUserState(u)
    saveUser(u)
  }, [])

  const logout = useCallback(() => {
    setUserState(null)
    saveUser(null)
    clearGuestSession()
    void logoutCliente()
    try {
      SESSION_KEYS.forEach((key) => localStorage.removeItem(key))
    } catch {
      /* ignore */
    }
  }, [])

  return (
    <UserContext.Provider value={{ user, setUser, logout }}>
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error('useUser must be used within UserProvider')
  return ctx
}
